import React, { useState, useEffect } from 'react';
import { XIcon, ClockIcon, VideoIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import type { Appointment, MeetingType } from '../../types/appointment';
import type { ClientRecord } from '../../hooks/useClients';

interface AppointmentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AppointmentFormData & { clientId: string }) => void;
  isSubmitting: boolean;
  editingAppointment?: Appointment | null;
  clientId?: string; // Optional - if not provided, show client dropdown
  clients?: ClientRecord[]; // For dropdown when clientId is null
}

export interface AppointmentFormData {
  title: string;
  notes: string;
  date: string; // YYYY-MM-DD
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  meeting_type: MeetingType;
  meeting_link: string;
  clientId?: string; // For when selected from dropdown
}

const initialFormState: AppointmentFormData = {
  title: '',
  notes: '',
  date: '',
  start_time: '',
  end_time: '',
  meeting_type: 'video',
  meeting_link: '',
  clientId: '',
};

function toDateInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function calculateDuration(start_time: string, end_time: string): string {
  if (!start_time || !end_time) return '';

  const [startHour, startMin] = start_time.split(':').map(Number);
  const [endHour, endMin] = end_time.split(':').map(Number);

  const startMins = startHour * 60 + startMin;
  const endMins = endHour * 60 + endMin;
  const diffMins = endMins - startMins;

  if (diffMins <= 0) return '';

  if (diffMins < 60) {
    return `${diffMins} min`;
  }
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function addMinutesToTime(time: string, minutes: number): string {
  if (!time) return '';
  const [hour, min] = time.split(':').map(Number);
  const totalMins = hour * 60 + min + minutes;
  const newHour = Math.floor(totalMins / 60) % 24;
  const newMin = totalMins % 60;
  return `${String(newHour).padStart(2, '0')}:${String(newMin).padStart(2, '0')}`;
}

export function AppointmentFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  editingAppointment,
  clientId,
  clients = [],
}: AppointmentFormModalProps) {
  const [formState, setFormState] = useState<AppointmentFormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);

  // Initialize form when editing
  useEffect(() => {
    if (editingAppointment) {
      setFormState({
        title: editingAppointment.title,
        notes: editingAppointment.notes || '',
        date: toDateInputValue(editingAppointment.starts_at),
        start_time: toTimeInputValue(editingAppointment.starts_at),
        end_time: toTimeInputValue(editingAppointment.ends_at),
        meeting_type: editingAppointment.meeting_type,
        meeting_link: editingAppointment.meeting_link || '',
      });
    } else {
      // Default to today and next hour for new appointments
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0);

      setFormState({
        ...initialFormState,
        date: toDateInputValue(now.toISOString()),
        start_time: toTimeInputValue(nextHour.toISOString()),
        end_time: addMinutesToTime(toTimeInputValue(nextHour.toISOString()), 30),
      });
    }
  }, [editingAppointment, isOpen]);

  const handleQuickDuration = (minutes: number) => {
    if (!formState.start_time) {
      setFormError('Please select a start time first');
      return;
    }
    const newEndTime = addMinutesToTime(formState.start_time, minutes);
    setFormState({ ...formState, end_time: newEndTime });
    setFormError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!clientId && !formState.clientId) {
      setFormError('Please select a client');
      return;
    }
    if (!formState.title.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!formState.date) {
      setFormError('Date is required');
      return;
    }
    if (!formState.start_time) {
      setFormError('Start time is required');
      return;
    }
    if (!formState.end_time) {
      setFormError('End time is required');
      return;
    }

    // Validate end time is after start time
    const [startHour, startMin] = formState.start_time.split(':').map(Number);
    const [endHour, endMin] = formState.end_time.split(':').map(Number);
    const startMins = startHour * 60 + startMin;
    const endMins = endHour * 60 + endMin;

    if (endMins <= startMins) {
      setFormError('End time must be after start time');
      return;
    }

    onSubmit({
      ...formState,
      clientId: clientId || formState.clientId || '',
    });
  };

  const handleClose = () => {
    setFormState(initialFormState);
    setFormError(null);
    onClose();
  };

  const duration = calculateDuration(formState.start_time, formState.end_time);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#212121]">
              {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
            </h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <XIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Client Selection (only when clientId not provided) */}
          {!clientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Client <span className="text-red-500">*</span>
              </label>
              <select
                value={formState.clientId}
                onChange={(e) => setFormState({ ...formState, clientId: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B9EAE] focus:border-transparent"
                disabled={isSubmitting}
              >
                <option value="">Select a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} {client.status && `(${client.status})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formState.title}
              onChange={(e) => setFormState({ ...formState, title: e.target.value })}
              placeholder="e.g., Initial Consultation"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formState.date}
              onChange={(e) => setFormState({ ...formState, date: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Time Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Time <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start</label>
                <input
                  type="time"
                  value={formState.start_time}
                  onChange={(e) => setFormState({ ...formState, start_time: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End</label>
                <input
                  type="time"
                  value={formState.end_time}
                  onChange={(e) => setFormState({ ...formState, end_time: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Duration Display */}
            {duration && (
              <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
                <ClockIcon className="w-4 h-4" />
                <span>Duration: {duration}</span>
              </div>
            )}

            {/* Quick Duration Buttons */}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => handleQuickDuration(30)}
                disabled={isSubmitting}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                30 min
              </button>
              <button
                type="button"
                onClick={() => handleQuickDuration(60)}
                disabled={isSubmitting}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                1 hour
              </button>
              <button
                type="button"
                onClick={() => handleQuickDuration(90)}
                disabled={isSubmitting}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                1.5 hours
              </button>
              <button
                type="button"
                onClick={() => handleQuickDuration(120)}
                disabled={isSubmitting}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                2 hours
              </button>
            </div>
          </div>

          {/* Meeting Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Meeting Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['in_person', 'video', 'phone'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormState({ ...formState, meeting_type: type })}
                  disabled={isSubmitting}
                  className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    formState.meeting_type === type
                      ? 'bg-[#5B8E87] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type === 'in_person' && <><MapPinIcon className="w-4 h-4" /><span>In-Person</span></>}
                  {type === 'video' && <><VideoIcon className="w-4 h-4" /><span>Video</span></>}
                  {type === 'phone' && <><PhoneIcon className="w-4 h-4" /><span>Phone</span></>}
                </button>
              ))}
            </div>
          </div>

          {/* Meeting Link (conditional) */}
          {formState.meeting_type === 'video' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Meeting Link
              </label>
              <input
                type="url"
                value={formState.meeting_link}
                onChange={(e) => setFormState({ ...formState, meeting_link: e.target.value })}
                placeholder="https://meet.google.com/..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notes
            </label>
            <textarea
              value={formState.notes}
              onChange={(e) => setFormState({ ...formState, notes: e.target.value })}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A870] focus:border-transparent resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Error Message */}
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">
              {formError}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white bg-[#C9A870] hover:bg-[#B89660] transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : editingAppointment ? 'Save Changes' : 'Create Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
}
